import {useDispatch} from 'react-redux';
import type {Sorting} from '@igp/shared/api';
import {useAutoMapper} from '@igp/shared/auto-mapper';
import type {DebtResponsibleMasterAgent} from '@igp/shared/contract/pam';
import {useAsyncActionState} from '@igp/shared/redux/async-actions';
import {RealtimeMessageTrigger} from '@igp/shared/redux/realtime';
import {extendedViewCleanDelay, type UseListViewEntityProps, type UseListViewEntityResult, useViewInit} from '@igp/shared/redux/view';
import {EntityType} from '@models';

import {debtResponsibleMasterAgentActions} from './actions';
import {debtResponsibleMasterAgentMappingKeys} from './mapper';
import type {
    AddDebtResponsibleMasterPayload,
    DebtResponsibleMasterAgentQueryFields,
    DebtResponsibleMasterAgentQueryFilterKeys,
    DebtResponsibleMasterAgentSortKeys,
    DebtResponsibleMasterAgentViewModel,
    DebtResponsibleMasterAgentViewModelKeys,
    RemoveDebtResponsibleMasterPayload,
} from './types';

export function useDebtResponsibleMasterAgents({
    viewType,
    fields,
    defaultFilters,
    defaultSorting,
    defaultPaging,
    syncWithUrl,
    validateFilter,
    cleanDelay = extendedViewCleanDelay,
    displayName,
    realtimeMode,
    isCancelable,
}: UseListViewEntityProps<DebtResponsibleMasterAgentQueryFilterKeys, DebtResponsibleMasterAgentViewModelKeys>): UseListViewEntityResult<
    DebtResponsibleMasterAgentViewModel,
    DebtResponsibleMasterAgentViewModelKeys
> {
    const mapper = useAutoMapper();
    const queryFields = fields.map(f =>
        mapper.map<DebtResponsibleMasterAgentViewModelKeys, DebtResponsibleMasterAgentQueryFields>(
            f,
            debtResponsibleMasterAgentMappingKeys.viewModelKeys,
            debtResponsibleMasterAgentMappingKeys.queryFields
        )
    );

    const {
        items: serverItems,
        totalCount,
        viewEntity,
        getSearchFilter,
        handleSortChange,
        handleFilterChange,
        handlePageChange,
        handlePageSizeChange,
    } = useViewInit<
        EntityType,
        DebtResponsibleMasterAgent,
        DebtResponsibleMasterAgentQueryFilterKeys,
        DebtResponsibleMasterAgentQueryFields,
        DebtResponsibleMasterAgentSortKeys
    >({
        viewType,
        entity: {entity: EntityType.DebtResponsibleMasterAgents, fields: queryFields},
        cleanDelay,
        defaultFilters,
        defaultPaging,
        defaultSorting: mapSorting(defaultSorting ?? [{field: 'created_at', sort: 'desc'}]),
        syncWithUrl,
        validateFilter,
        displayName,
        realtime: realtimeMode
            ? {
                  entity: EntityType.DebtResponsibleMasterAgents,
                  mode: realtimeMode,
                  triggers: [
                      {
                          type: RealtimeMessageTrigger.Add,
                      },
                      {
                          type: RealtimeMessageTrigger.Delete,
                      },
                  ],
              }
            : null,
        isCancelable,
    });

    const items = serverItems.map(i =>
        mapper.tryMap<DebtResponsibleMasterAgent, DebtResponsibleMasterAgentViewModel>(
            i,
            debtResponsibleMasterAgentMappingKeys.serverModel,
            debtResponsibleMasterAgentMappingKeys.viewModel
        )
    );
    const searchFilter = getSearchFilter(i =>
        mapper.map<DebtResponsibleMasterAgentSortKeys, DebtResponsibleMasterAgentViewModelKeys>(
            i,
            debtResponsibleMasterAgentMappingKeys.sortKeys,
            debtResponsibleMasterAgentMappingKeys.viewModelKeys
        )
    );

    function mapSorting(sorting: Sorting<DebtResponsibleMasterAgentViewModelKeys>[]): Sorting<DebtResponsibleMasterAgentSortKeys>[] {
        return (
            sorting?.map(s => ({
                field: mapper.map<DebtResponsibleMasterAgentViewModelKeys, DebtResponsibleMasterAgentSortKeys>(
                    s.field,
                    debtResponsibleMasterAgentMappingKeys.viewModelKeys,
                    debtResponsibleMasterAgentMappingKeys.sortKeys
                ),
                sort: s.sort,
            })) ?? []
        );
    }

    function handleVmSortChange(sorting: Sorting<DebtResponsibleMasterAgentViewModelKeys>[]) {
        handleSortChange(mapSorting(sorting));
    }

    return {
        items: items?.map(i => i.model),
        rawItems: items?.map(i => ({isValid: i.isModelValid, raw: i.rawModelString})),
        totalCount,
        searchFilter,
        filterString: viewEntity?.filter,
        handleSortChange: handleVmSortChange,
        handleFilterChange,
        handlePageChange,
        handlePageSizeChange,
    };
}

type UseDebtResponsibleMasterAgentActionsResult = {
    isAddInProgress: boolean;
    isRemoveInProgress: boolean;
    addDebtResponsibleMasterAgent: (model: AddDebtResponsibleMasterPayload) => void;
    removeDebtResponsibleMasterAgent: (model: RemoveDebtResponsibleMasterPayload) => void;
};

export function useDebtResponsibleMasterAgentActions(): UseDebtResponsibleMasterAgentActionsResult {
    const dispatch = useDispatch();
    const {isProgress: isAddInProgress} = useAsyncActionState(debtResponsibleMasterAgentActions.addAgent);
    const {isProgress: isRemoveInProgress} = useAsyncActionState(debtResponsibleMasterAgentActions.removeAgent);

    function addDebtResponsibleMasterAgent(model: AddDebtResponsibleMasterPayload) {
        dispatch(debtResponsibleMasterAgentActions.addAgent.request(model));
    }

    function removeDebtResponsibleMasterAgent(model: RemoveDebtResponsibleMasterPayload) {
        dispatch(debtResponsibleMasterAgentActions.removeAgent.request(model));
    }

    return {
        isAddInProgress,
        isRemoveInProgress,
        addDebtResponsibleMasterAgent,
        removeDebtResponsibleMasterAgent,
    };
}
